import { ehPorAcoes } from '@social/shared';
import type {
  CriarSociedadeDto,
  EspecieAcao,
  PapelSocioComandita,
  Sociedade,
  TipoSocietario,
} from '@social/shared';

/**
 * Peças do cadastro de sociedade que valem para os três blocos do formulário
 * (dados, sócios, administradores) — e os porquês que não cabem num comentário
 * de linha.
 */

/**
 * Os tipos que o cadastro OFERECE — e não todos os que o enum aceita.
 *
 * O nome não é mais "com contrato": a S.A. entrou, e ela se constitui por
 * ESTATUTO. O que estes seis têm em comum é que o gerador sabe montar o
 * documento de constituição de cada um — contrato para cinco, estatuto para a
 * companhia. `EI` fica de fora porque é requerimento de empresário, não ato
 * constitutivo de sociedade; `SCP` não registra na Junta.
 *
 * Oferecer um tipo que o gerador não monta seria a "promessa falsa" da regra 6
 * da skill `social-desenho`: o cadastro aceitaria, e a geração sairia errada
 * ou não sairia — e quem descobre é o contador, com o cliente esperando.
 */
export const TIPOS_OFERECIDOS = [
  'SLU',
  'LTDA',
  'SS',
  'NC',
  'CS',
  'SA',
] as const satisfies readonly TipoSocietario[];

export type TipoOferecido = (typeof TIPOS_OFERECIDOS)[number];

/** Quantos sócios cada tipo exige. `SLU` é unipessoal por definição. */
export const SOCIOS_MINIMOS: Record<TipoOferecido, number> = {
  SLU: 1,
  LTDA: 2,
  SS: 2,
  // Nome Coletivo e Comandita Simples: dois ou mais, arts. 1.039 e 1.045 CC.
  // A regra `nc-cs-unipessoal` do validador reprova o contrário.
  NC: 2,
  CS: 2,
  // Companhia se constitui com dois subscritores no mínimo (art. 80, I, da
  // Lei 6.404/76). A regra `sa-com-menos-de-dois-acionistas` reprova o resto.
  SA: 2,
};

/**
 * Os tipos em que o sócio tem PAPEL — hoje só a Comandita Simples.
 *
 * Na comandita os dois papéis são a essência do tipo: o comanditado responde
 * ilimitadamente e administra; o comanditário responde até o valor da quota e
 * NÃO pode administrar (arts. 1.045 e 1.047 CC). Sem declarar quem é quem, o
 * validador reprova o cadastro — e com razão, porque o contrato não teria como
 * dizer quem responde pelo quê.
 */
export const TIPOS_COM_PAPEL_DE_SOCIO: readonly TipoOferecido[] = ['CS'];

/**
 * O capital é DERIVADO das quotas, nunca digitado.
 *
 * A regra `capital-nao-bate` do validador exige
 * `soma(quotas) × valorNominal === capitalCentavos`. Pedir o capital num campo
 * próprio é convidar a divergência: a pessoa preenche as quotas, preenche o
 * capital "redondo", e o laudo reprova por centavos.
 *
 * Aqui a tela calcula e MOSTRA o número. Quem controla o total é quem
 * distribui as quotas, que é como a coisa funciona no papel também.
 */
export function capitalDerivado(
  quotasPorSocio: readonly number[],
  valorNominalCentavos: number,
): number {
  const total = quotasPorSocio.reduce(
    (soma, q) => soma + (Number.isFinite(q) ? q : 0),
    0,
  );
  return total * valorNominalCentavos;
}

/** Reais para centavos, sem passar por float. */
export function centavosDeReais(texto: string): number {
  const limpo = texto
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const n = Number(limpo);
  if (!Number.isFinite(n)) return 0;
  // `Math.round` porque `19.99 * 100` dá 1998.9999999999998 em ponto
  // flutuante — e um centavo a menos reprova a aritmética do validador.
  return Math.round(n * 100);
}

export const moedaBR = (centavos: number): string =>
  (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Hoje em ISO, para os campos de data nascerem preenchidos com o provável. */
export function hojeISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * O rascunho que o formulário manipula.
 *
 * Difere do `CriarSociedadeDto` em um ponto de propósito: aqui as quantias
 * são TEXTO, porque é o que um `<input>` devolve. A conversão para centavos
 * acontece uma vez, no envio — espalhá-la pelos campos faria cada um decidir
 * sozinho como arredondar.
 */
export interface RascunhoSociedade {
  tipo: TipoOferecido;
  nomeEmpresarial: string;
  nomeFantasia: string;
  inicioAtividades: string;
  objetoSocial: string;
  enquadramento: CriarSociedadeDto['enquadramento'];
  valorNominalQuota: string;
  foroComarca: string;
  foroUf: string;
  sede: CriarSociedadeDto['sede'];
  atividades: { cnae: string; descricao: string; principal: boolean }[];
  socios: {
    pessoaId: string;
    quotas: string;
    integralizadas: string;
    /** Só na Comandita Simples — ver `TIPOS_COM_PAPEL_DE_SOCIO`. */
    papelSocio?: PapelSocioComandita;
    /** Só na S.A. Ausente = ordinária, que é o padrão da lei. */
    especie?: EspecieAcao;
    /** Classe da preferencial (`A`, `B`…). */
    classe?: string;
  }[];
  administradores: {
    pessoaId: string;
    usoIsoladoDoNome: boolean;
    vedadoFiancaAvalEndosso: boolean;
    podeConstituirProcuradores: boolean;
  }[];
  /**
   * O regime do estatuto. Só na S.A., e sempre presente quando o tipo é S.A.
   * — diferente das filiais, aqui não há "nenhum": toda companhia tem um
   * regime, mesmo que seja o padrão da lei inteiro.
   */
  acoes?:
    | {
        aberta: boolean;
        capitalAutorizado: string;
        conselhoAdministracao: boolean;
        conselheiros: string;
        conselhoFiscalPermanente: boolean;
        dividendoMinimo: string;
        vantagemPreferenciais: string;
      }
    /*
      `| undefined` explícito, e não só `?`. Com `exactOptionalPropertyTypes`,
      a troca de tipo societário precisa PODER apagar o regime — e apagar é
      mandar `acoes: undefined` pelo mesmo `mudar` que todo o resto usa.
    */
    | undefined;
  /** Filiais em edição. Ausente = nenhuma, que é o caso comum. */
  filiais?: {
    logradouro: string;
    numero: string;
    bairro: string;
    municipio: string;
    uf: string;
    cep: string;
    cnpj: string;
    capitalDestacado: string;
    abertura: string;
  }[];
}

export /**
 * O rascunho da tela virando o DTO da API — usado pela constituição e pela
 * alteração, que enviam exatamente a mesma forma de estado.
 */
function montarDto(
  r: RascunhoSociedade,
  valorNominalCentavos: number,
  capitalCentavos: number,
): CriarSociedadeDto {
  // O papel só vai quando o TIPO o exige: mandá-lo numa LTDA gravaria um
  // "comanditado" em sociedade que não tem comanditados, e o dado sobreviveria
  // à troca de tipo sem ninguém notar.
  const levaPapel = TIPOS_COM_PAPEL_DE_SOCIO.includes(r.tipo);
  // Mesma razão do papel: espécie de ação gravada numa limitada é dado morto
  // que o laudo denuncia (`especie-de-acao-em-tipo-sem-acoes`) e que
  // sobreviveria a uma transformação sem ninguém notar.
  const porAcoes = ehPorAcoes(r.tipo);
  const socios = r.socios.map((s) => ({
    pessoaId: s.pessoaId,
    quotas: Number(s.quotas) || 0,
    quotasIntegralizadas: Number(s.integralizadas) || 0,
    entrada: r.inicioAtividades,
    incomunicavel: true,
    impenhoravel: true,
    ...(levaPapel && s.papelSocio ? { papelSocio: s.papelSocio } : {}),
    ...(porAcoes && s.especie ? { especie: s.especie } : {}),
    ...(porAcoes && s.especie === 'PREFERENCIAL' && s.classe ? { classe: s.classe } : {}),
  }));

  return {
    tipo: r.tipo,
    nomeEmpresarial: r.nomeEmpresarial,
    ...(r.nomeFantasia ? { nomeFantasia: r.nomeFantasia } : {}),
    inicioAtividades: r.inicioAtividades,
    fimPrazoDuracao: null,
    sede: r.sede,
    objetoSocial: r.objetoSocial,
    atividades: r.atividades,
    capitalCentavos,
    valorNominalQuotaCentavos: valorNominalCentavos,
    enquadramento: r.enquadramento,
    foroComarca: r.foroComarca,
    foroUf: r.foroUf,
    /*
      Só entra quando há alguma: `filiais: []` em toda sociedade sem filial
      poluiria o corpo e faria o diff comparar array vazio com ausente como se
      fossem coisas diferentes.
    */
    ...(r.filiais && r.filiais.length > 0
      ? {
          filiais: r.filiais.map((f) => ({
            endereco: {
              logradouro: f.logradouro,
              numero: f.numero,
              bairro: f.bairro,
              municipio: f.municipio,
              uf: f.uf,
              cep: f.cep,
            },
            ...(f.cnpj ? { cnpj: f.cnpj } : {}),
            ...(centavosDeReais(f.capitalDestacado) > 0
              ? { capitalDestacadoCentavos: centavosDeReais(f.capitalDestacado) }
              : {}),
            abertura: f.abertura,
          })),
        }
      : {}),
    ...(porAcoes && r.acoes
      ? {
          acoes: {
            aberta: r.acoes.aberta,
            ...(centavosDeReais(r.acoes.capitalAutorizado) > 0
              ? { capitalAutorizadoCentavos: centavosDeReais(r.acoes.capitalAutorizado) }
              : {}),
            conselhoAdministracao: r.acoes.conselhoAdministracao,
            ...(r.acoes.conselhoAdministracao && Number(r.acoes.conselheiros) >= 3
              ? { conselheiros: Number(r.acoes.conselheiros) }
              : {}),
            conselhoFiscalPermanente: r.acoes.conselhoFiscalPermanente,
            // O campo da tela é percentual; o modelo guarda centésimos de
            // ponto, para que 12,5% não vire float.
            dividendoMinimoBps: Math.round((Number(r.acoes.dividendoMinimo) || 0) * 100),
            ...(r.acoes.vantagemPreferenciais
              ? { vantagemPreferenciais: r.acoes.vantagemPreferenciais }
              : {}),
          },
        }
      : {}),
    socios,
    administradores: r.administradores.map((a) => ({
      pessoaId: a.pessoaId,
      // Administrador que também é sócio não precisa de quórum de aprovação
      // (art. 1.061 CC) — e é o caso comum na constituição.
      ehSocio: socios.some((s) => s.pessoaId === a.pessoaId),
      inicio: r.inicioAtividades,
      usoIsoladoDoNome: a.usoIsoladoDoNome,
      vedadoFiancaAvalEndosso: a.vedadoFiancaAvalEndosso,
      podeConstituirProcuradores: a.podeConstituirProcuradores,
      declaraDesimpedimento: true,
    })),
  } as CriarSociedadeDto;
}

/**
 * O caminho INVERSO: a sociedade gravada virando rascunho de tela.
 *
 * É o que a alteração precisa — ela não começa em branco, começa no que a
 * sociedade é hoje. Sem isto, alterar o endereço obrigaria a redigitar sócios,
 * capital e objeto social, e cada redigitação é uma chance de mudar sem
 * querer um campo que ninguém pediu para mudar.
 *
 * Os números viram texto porque o rascunho é o que está NA TELA, e campo de
 * texto guarda texto: converter cedo transformaria um campo em edição
 * ("1.00") em `NaN` no meio da digitação.
 */
export function rascunhoDe(s: Sociedade): RascunhoSociedade {
  return {
    tipo: s.tipo as TipoOferecido,
    nomeEmpresarial: s.nomeEmpresarial,
    nomeFantasia: s.nomeFantasia ?? '',
    inicioAtividades: s.inicioAtividades,
    objetoSocial: s.objetoSocial,
    enquadramento: s.enquadramento,
    valorNominalQuota: (s.valorNominalQuotaCentavos / 100).toFixed(2),
    foroComarca: s.foroComarca,
    foroUf: s.foroUf,
    sede: s.sede,
    atividades: s.atividades.map((a) => ({
      cnae: a.cnae,
      descricao: a.descricao,
      principal: a.principal,
    })),
    // Só os sócios VIGENTES: quem já se retirou não volta para a tela como se
    // ainda estivesse na sociedade. A saída dele continua gravada no ato que a
    // registrou — é lá que mora o histórico.
    socios: s.socios
      .filter((p) => !p.saida)
      .map((p) => ({
        pessoaId: p.pessoaId,
        quotas: String(p.quotas),
        integralizadas: String(p.quotasIntegralizadas),
        ...(p.papelSocio ? { papelSocio: p.papelSocio } : {}),
        ...(p.especie ? { especie: p.especie } : {}),
        ...(p.classe ? { classe: p.classe } : {}),
      })),
    ...(s.acoes
      ? {
          acoes: {
            aberta: s.acoes.aberta,
            capitalAutorizado: s.acoes.capitalAutorizadoCentavos
              ? (s.acoes.capitalAutorizadoCentavos / 100).toFixed(2)
              : '',
            conselhoAdministracao: s.acoes.conselhoAdministracao,
            conselheiros: s.acoes.conselheiros ? String(s.acoes.conselheiros) : '3',
            conselhoFiscalPermanente: s.acoes.conselhoFiscalPermanente,
            dividendoMinimo: (s.acoes.dividendoMinimoBps / 100).toFixed(2),
            vantagemPreferenciais: s.acoes.vantagemPreferenciais ?? '',
          },
        }
      : {}),
    // Só as VIGENTES: filial encerrada fica no histórico do ato que a fechou,
    // e trazê-la de volta ao formulário a reabriria na próxima alteração.
    ...(s.filiais && s.filiais.some((f) => !f.encerramento)
      ? {
          filiais: s.filiais
            .filter((f) => !f.encerramento)
            .map((f) => ({
              logradouro: f.endereco.logradouro,
              numero: f.endereco.numero,
              bairro: f.endereco.bairro,
              municipio: f.endereco.municipio,
              uf: f.endereco.uf,
              cep: f.endereco.cep,
              cnpj: f.cnpj ?? '',
              capitalDestacado: f.capitalDestacadoCentavos
                ? (f.capitalDestacadoCentavos / 100).toFixed(2)
                : '',
              abertura: f.abertura,
            })),
        }
      : {}),
    administradores: s.administradores
      .filter((a) => !a.fim)
      .map((a) => ({
        pessoaId: a.pessoaId,
        usoIsoladoDoNome: a.usoIsoladoDoNome,
        vedadoFiancaAvalEndosso: a.vedadoFiancaAvalEndosso,
        podeConstituirProcuradores: a.podeConstituirProcuradores,
      })),
  };
}
